import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { env } from '../../../config/env';
import { integrationStatusService } from '../../integrations/services/integrationStatusService';
import { LMS_CALENDAR_UPDATED_EVENT } from '../../../services/lmsService';
import { fetchIcsText } from '../services/fetchIcsText';
import { fetchLmsIcsForUser } from '../services/fetchLmsIcsForUser';
import { getLmsIcsErrorInfo } from '../services/lmsIcsFetchError';
import { dedupeLmsCalendarEvents } from '../utils/eventSelection';
import { parseIcs } from '../utils/parseIcs';

/** Keep ICS data until user clicks Refresh or revokes/relinks LMS (invalidate). */
export const LMS_ICS_STALE_MS = Infinity;

/** Hold cache in memory for a day after last observer unmounts. */
export const LMS_ICS_GC_MS = 1000 * 60 * 60 * 24;

/** @param {string} [userId] */
export function lmsIcsQueryKey(userId) {
    return ['lms-ics', userId];
}

/**
 * Shared fetcher for prefetch + useQuery (dedupes in-flight requests by key).
 *
 * @param {string} userId
 * @param {AbortSignal} [signal]
 */
export async function fetchLmsIcsEventsData(userId, signal) {
    const status = await integrationStatusService.getLmsCalendarStatus(userId);
    if (!status.connected || !status.feedUrl) {
        return {
            feedConnected: false,
            feedUrl: null,
            username: status.username,
            calendarName: null,
            events: [],
            icsFetchFailed: false,
        };
    }
    try {
        const base = (env.backendBaseUrl || '').replace(/\/$/, '');
        const text = base
            ? await fetchLmsIcsForUser(userId, signal)
            : await fetchIcsText(status.feedUrl, signal);
        const { calendarName, events } = parseIcs(text);
        return {
            feedConnected: true,
            feedUrl: status.feedUrl,
            username: status.username,
            calendarName,
            events: dedupeLmsCalendarEvents(events),
            icsFetchFailed: false,
        };
    } catch (e) {
        const info = getLmsIcsErrorInfo(e);
        return {
            feedConnected: true,
            feedUrl: status.feedUrl,
            username: status.username,
            calendarName: null,
            events: [],
            icsFetchFailed: true,
            icsErrorCode: info.code,
            icsErrorMessage: info.message,
        };
    }
}

/**
 * Loads LMS row (ics_feed_url), fetches ICS via API or direct, parses events.
 * Cached aggressively: no refetch on mount/focus/reconnect; use Refetch button or LMS link event.
 *
 * @param {string} [userId]
 */
export function useLmsIcsEvents(userId) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: lmsIcsQueryKey(userId),
        queryFn: ({ signal }) => fetchLmsIcsEventsData(userId, signal),
        enabled: Boolean(userId),
        staleTime: LMS_ICS_STALE_MS,
        gcTime: LMS_ICS_GC_MS,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (!userId) return undefined;
        const onLmsUpdated = () => {
            queryClient.invalidateQueries({ queryKey: lmsIcsQueryKey(userId) });
        };
        window.addEventListener(LMS_CALENDAR_UPDATED_EVENT, onLmsUpdated);
        return () => window.removeEventListener(LMS_CALENDAR_UPDATED_EVENT, onLmsUpdated);
    }, [userId, queryClient]);

    return query;
}
